import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    Subject,
    Subscription,
    fromEvent,
    merge,
    of,
    timer,
} from 'rxjs';
import {
    catchError,
    exhaustMap,
    filter,
    finalize,
    switchMap,
    tap,
    throttleTime,
} from 'rxjs/operators';
import {
    LatestCriticalMessageModel,
    MarkAllNotificationsReadModel,
    MarkNotificationReadModel,
    MyNotificationsPagedModel,
    MyNotificationsRequestModel,
    UnreadNotificationsCountModel,
} from 'core/models/my-notification.model';
import { ApiMyNotificationsService } from './api/api.my-notifications.service';

const pageResumeThrottleMs = 1000;

export interface MyNotificationsMonitorState {
    unreadCount: number;
}

export interface MyNotificationAlertPayload {
    unreadCount: number;
    latestCriticalMessage: LatestCriticalMessageModel | null;
}

@Injectable({
    providedIn: 'root',
})
export class MyNotificationsMonitorService {
    public readonly pollIntervalMs = 5 * 60 * 1000;
    public readonly state$ = new BehaviorSubject<MyNotificationsMonitorState>({ unreadCount: 0 });
    public readonly alert$ = new Subject<MyNotificationAlertPayload>();

    private readonly schedule$ = new Subject<number>();
    private pollSubscription = new Subscription();
    private monitoring = false;
    private lastRequestAt: number | null = null;
    private previousCount: number | null = null;
    private previousCriticalId: number | null = null;

    constructor(private apiMyNotificationsService: ApiMyNotificationsService) {}

    public start(): void {
        if (this.monitoring) {
            return;
        }

        this.monitoring = true;
        this.pollSubscription = this.schedule$
            .pipe(
                switchMap(delayMs => timer(delayMs)),
                filter(() => !document.hidden),
                exhaustMap(() => this.requestUnreadCount()),
            )
            .subscribe();
        this.pollSubscription.add(
            this.getPageResumeEvents()
                .pipe(tap(() => this.scheduleNextRequest()))
                .subscribe(),
        );
        this.scheduleNextRequest();
    }

    public stop(): void {
        this.monitoring = false;
        this.pollSubscription.unsubscribe();
        this.pollSubscription = new Subscription();
        this.lastRequestAt = null;
        this.previousCount = null;
        this.previousCriticalId = null;
        this.state$.next({ unreadCount: 0 });
    }

    public applyCount(unreadCount: number): void {
        const normalizedCount = Math.max(0, unreadCount);

        this.previousCount = normalizedCount;
        this.state$.next({ unreadCount: normalizedCount });
    }

    public loadList(params: MyNotificationsRequestModel): Observable<MyNotificationsPagedModel> {
        return this.apiMyNotificationsService
            .getList(params)
            .pipe(tap(response => this.applyCount(response.unreadCount)));
    }

    public markRead(id: number): Observable<MarkNotificationReadModel> {
        return this.apiMyNotificationsService
            .markRead(id)
            .pipe(tap(() => this.applyCount(this.state$.value.unreadCount - 1)));
    }

    public markAllRead(): Observable<MarkAllNotificationsReadModel> {
        return this.apiMyNotificationsService
            .markAllRead()
            .pipe(tap(response => this.applyCount(response.unreadCount)));
    }

    private getPageResumeEvents(): Observable<Event> {
        return merge(fromEvent(document, 'visibilitychange'), fromEvent(window, 'focus')).pipe(
            throttleTime(pageResumeThrottleMs),
        );
    }

    private scheduleNextRequest(): void {
        if (!this.monitoring) {
            return;
        }

        this.schedule$.next(this.getDelayUntilNextRequest());
    }

    private getDelayUntilNextRequest(): number {
        if (this.lastRequestAt === null) {
            return 0;
        }

        return Math.max(0, this.pollIntervalMs - (Date.now() - this.lastRequestAt));
    }

    private requestUnreadCount(): Observable<UnreadNotificationsCountModel | null> {
        this.lastRequestAt = Date.now();

        return this.apiMyNotificationsService.getUnreadCount().pipe(
            tap(response => this.applyUnreadCountResponse(response)),
            catchError(() => of(null)),
            finalize(() => this.scheduleNextRequest()),
        );
    }

    private applyUnreadCountResponse(response: UnreadNotificationsCountModel): void {
        const unreadCount = Math.max(0, response?.unreadCount ?? 0);
        const latestCriticalMessage = response?.latestCriticalMessage ?? null;
        const isFirstResponse = this.previousCount === null;
        const hasNewNotifications = isFirstResponse
            ? unreadCount > 0
            : unreadCount > this.previousCount;
        const hasNewCriticalMessage =
            !!latestCriticalMessage &&
            (isFirstResponse || latestCriticalMessage.id !== this.previousCriticalId);

        this.previousCount = unreadCount;
        this.previousCriticalId = latestCriticalMessage?.id ?? this.previousCriticalId;
        this.state$.next({ unreadCount });

        if (!hasNewNotifications) {
            return;
        }

        this.alert$.next({
            unreadCount,
            latestCriticalMessage: hasNewCriticalMessage ? latestCriticalMessage : null,
        });
    }
}
